// Copyright (c) Ping. All rights reserved.
//
// Mid-call "Invite to call" picker — Figma DS "In call invite menu" (889:13425).
// Search the directory and invite people into the ongoing call: their devices
// are added to the per-call MLS group and a fresh call_invite rings them
// (callStore.inviteToCall). "Copy invite link" copies a shareable link.

import { useEffect, useState } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';

import { Check, Search, X } from 'lucide-react-native';

import { Avatar } from '@ping/ui';

import { useCallStore } from '@calls/store/callStore';
import { type DirectoryUser,searchUsers } from '@core/network/users';
import { toastStore } from '@features/toasts/toastStore';

import { useCallIconColors } from './callTheme';

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

export function InviteMenu({ onClose }: { onClose: () => void }): JSX.Element {
  const ic = useCallIconColors();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [invited, setInvited] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    const id = setTimeout(() => {
      void searchUsers({ query, limit: 8 })
        .then((res) => {
          if (alive) setUsers([...res.users]);
        })
        .catch(() => undefined);
    }, 200);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [query]);

  const invite = (u: DirectoryUser) => {
    void useCallStore.getState().inviteToCall([{ accountId: u.id, displayName: u.displayName }]);
    setInvited((s) => new Set(s).add(u.id));
  };

  const copyLink = () => {
    const callId = useCallStore.getState().active?.callId ?? '';
    const link = `${typeof location !== 'undefined' ? location.origin : ''}/call/${callId}`;
    void navigator.clipboard?.writeText(link).catch(() => undefined);
    toastStore.show('Invite link copied', { type: 'success' });
  };

  return (
    <View className="h-full w-[300px] rounded-[12px] border border-[#E1E3E8] dark:border-[#2F333B] bg-white dark:bg-[#15171C] p-[12px]">
      <View className="h-[28px] flex-row items-center justify-between">
        <Text className="text-[16px] text-[#6B7280]">Invite to call</Text>
        <Pressable accessibilityLabel="Close invite" onPress={onClose}>
          <X size={20} color={ic.muted} />
        </Pressable>
      </View>

      <View className="mt-[8px] flex-row items-center gap-[8px] rounded-[10px] border border-[#E1E3E8] dark:border-[#2F333B] bg-[#F2F3F5] dark:bg-[#222429] px-[10px]">
        <Search size={16} color={ic.muted} />
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder="Search..."
          placeholderTextColor="#6B7280"
          className="h-[36px] flex-1 text-[14px] text-white"
        />
      </View>

      <View className="mt-[8px] flex-1">
        {users.map((u) => {
          const done = invited.has(u.id);
          return (
            <View key={u.id} className="flex-row items-center gap-[10px] py-[8px]">
              <Avatar
                size="tile"
                initials={initials(u.displayName)}
                {...(u.avatarUri !== undefined ? { imageUri: u.avatarUri } : {})}
              />
              <Text className="flex-1 text-[14px] text-[#15171C] dark:text-[#F5F6F8]" numberOfLines={1}>
                {u.displayName}
              </Text>
              <Pressable
                disabled={done}
                onPress={() => invite(u)}
                className={`h-[28px] flex-row items-center gap-[4px] rounded-[8px] px-[12px] ${
                  done ? 'bg-[#F2F3F5] dark:bg-[#222429]' : 'bg-[#537EFF] active:opacity-80'
                }`}
              >
                {done ? <Check size={14} color="#3CCB4F" /> : null}
                <Text className="text-[13px] font-bold text-white">{done ? 'Invited' : 'Invite'}</Text>
              </Pressable>
            </View>
          );
        })}
      </View>

      <Pressable
        onPress={copyLink}
        className="mt-[8px] items-center rounded-[8px] border border-[#E1E3E8] dark:border-[#2F333B] py-[8px] active:bg-black/[0.05] dark:active:bg-white/[0.06]"
      >
        <Text className="text-[14px] text-[#15171C] dark:text-[#F5F6F8]">Copy invite link</Text>
      </Pressable>
    </View>
  );
}
